import { Grid } from '@mui/material';
import { PageSelector, RecordsPerPageSelector, useAgGridPagination } from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import { BasicTableOptions } from '@tassat/common-definitions';
import { Activity } from '@tassat/fred-definitions';
import { Export as ExportIcon } from '@tassat/icon';
import React from 'react';
import { Route } from 'react-router-dom';

import { DEFAULT_ITEMS_PER_PAGE } from 'Lib/constant';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getUsers } from 'Selectors/common';
import { TableOptionsContext, tableOptionsReducer } from 'SharedComponents/contexts/TableOptionsContext';

import FirmActivityLogsAgGrid from './firmActivityLogsAgGrid';

import type { FirmActivityLogProps } from '.';

interface TableOptions {
    PageNo: number;
    RecordPerPage: number;
    Sort: string;
    Condition: string;
}
const tableOptionsInitialState: BasicTableOptions = {
    PageNo: 1,
    RecordPerPage: DEFAULT_ITEMS_PER_PAGE,
    Sort: 'auditTime|DESC',
    DefaultSort: 'auditTime|DESC',
    SelectedDateFilter: '',
    DateCondition: '',
    SelectedFilter: 'none',
    FilterCondition: '',
    TableFilters: [],
    DefaultState: true
};

export function FirmActivityLogs(props: FirmActivityLogProps) {
    const {
        classes,
        firm
    } = props;
    const users = useAppSelector(getUsers);
    const [ rowCount, setRowCount ] = React.useState<number>(0);
    const [ TableOptions, TableOptionsDispatch ] = React.useReducer<(TableOptions, { type, values }: { type: string; values: object }) =>
    BasicTableOptions>(tableOptionsReducer, tableOptionsInitialState);
    const tableContextMemo = React.useMemo(() => {
        return {
            TableOptions,
            TableOptionsDispatch
        };
    }, [ TableOptions, TableOptionsDispatch ]);
    const [ setPageNum, setRecordsPerPage ] = useAgGridPagination(TableOptionsDispatch);
    const [ tableData, setTableData ] = React.useState<Activity[]>([]);
    const [ exportData, setExportData ] = React.useState<boolean>(false);

    React.useEffect(() => {
        fetchApiTransactions();
    }, [
        TableOptions.PageNo,
        TableOptions.RecordPerPage,
        TableOptions.FilterCondition,
        TableOptions.Sort
    ]);

    const fetchApiTransactions = async () => {
        const {
            PageNo,
            RecordPerPage,
            FilterCondition,
            Sort
        } = TableOptions;

        if (!PageNo) return;

        const res = await Worker.getInstance()
            .getFirmActivity({
                PageNo,
                RecordPerPage,
                Sort,
                Condition: `firmId|EQ|${firm.FirmId}${FilterCondition ? `:${FilterCondition}` : ''}`
            });

        let activities = [];
        if (res.data && res.data.activities) {
            activities = res.data.activities.map(activity => {
                const user = users[activity.RequesterId];
                activity.Email = user ? user.Email : '-';

                return activity;
            });
        }

        setTableData(activities);
        setRowCount(res.data.TotalCount);

        return res;
    };

    return (
        <>
            <Route
                exact
                path='/admin/firms/:firmId'
                render={props => (
                    <TableOptionsContext.Provider value={tableContextMemo}>
                        <Grid
                            className={classes.root}
                            container
                            direction='column'
                            justifyContent='flex-start'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Grid
                                className={classes.gridContainer}
                                container
                                item
                                justifyContent='center'
                                spacing={0}
                            >
                                <Grid
                                    className={classes.grid}
                                    item
                                    xs={10}
                                >
                                    <ToolbarButton
                                        className={classes.exportButton}
                                        Icon={ExportIcon}
                                        iconClassName={classes.exportIcon}
                                        onClick={() => setExportData(true)}
                                        text='Export'
                                    />
                                    <FirmActivityLogsAgGrid
                                        activities={tableData}
                                        exportData={exportData}
                                        firm={firm}
                                        setExportData={setExportData}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                className={classes.pageSelectorContainer}
                                container
                                direction='row'
                                item
                                justifyContent='center'
                            >
                                <Grid
                                    container
                                    direction='row'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                    xs={10}
                                >
                                    <RecordsPerPageSelector
                                        currentRecordsPerPage={TableOptions.RecordPerPage}
                                        setRecordsPerPage={setRecordsPerPage}
                                    />
                                    <PageSelector
                                        changePageOnApplyOnly
                                        currentPageNum={TableOptions.PageNo}
                                        onPageChange={setPageNum}
                                        recordsPerPage={TableOptions.RecordPerPage}
                                        totalRows={rowCount}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </TableOptionsContext.Provider>
                )}
            />
        </>
    );
}